import * as React from 'react';
import { connect } from 'react-redux'; // Connecting to Redux
import { Dispatch } from 'redux';
import { Link } from 'react-router-dom';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { Tooltip, message, Form, Input, Row, Col, Popover } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import Button from 'antd/lib/button/button';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import { NameOf } from 'src/utils/NameOf';
import { downloadFile } from 'src/utils/DownloadUtil';
import Routes from 'src/core/Routes';
import { PageProps } from 'src/models/common/ComponentProps';

import DataTable, {
    DataTableColumnProps,
    Renderer,
    Filterer,
    DataTableButton,
    DataTableButtonReset
} from 'src/components/common/DataTable';

import TableRequestDTO from 'src/models/datatable/TableRequestDTO';
import TableResponseDTO from 'src/models/datatable/TableResponseDTO';
import UserAction, { EmulateUserType } from "src/redux/UserActions";
import UserApiController from 'src/api/UserApiController';
import StateApiController from 'src/api/StateApiController';
import ReportApiController from 'src/api/ReportApiController';
import DistrictApiController from 'src/api/DistrictApiController';
import SchoolApiController from 'src/api/SchoolApiController';
import ClientApiController from 'src/api/ClientApiController';
import UniversalUserSearchDTO from 'src/models/user/UniversalUserSearchDTO';
import UniversalUserSearchForm, { UnitSearchProps } from './UniversalUserSearchForm';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import { Roles } from 'src/constants/Roles';
import { AddEditUserStateParams } from './AddEditUser';
import { ResetPasswordStateParams } from './universalSearch/USResetPassword';


interface UniversalUserSearchProps extends PageProps<{}>, FormComponentProps, LocalizeContextProps {
    emulateUser: (id: number, type: EmulateUserType) => Promise<boolean>;
}

interface UniversalUserSearchState {
    isLoading: boolean;
    isFilterLoading: boolean;
    searchParams: UnitSearchProps;

    // Fetched Data
    states: KeyValueDTO<number, string>[];
    districts: KeyValueDTO<number, string>[];
    schools: KeyValueDTO<number, string>[];
    clients: KeyValueDTO<number, string>[];
}

class UniversalUserSearch extends React.Component<UniversalUserSearchProps, UniversalUserSearchState> {
    private standardDataTableRef: DataTable<UniversalUserSearchDTO>;

    constructor(props: UniversalUserSearchProps) {
        super(props);
        bindAllOfThis(this, UniversalUserSearch.prototype);

        this.state = {
            isLoading: false,
            isFilterLoading: false,
            searchParams: {},
            states: [],
            districts: [],
            schools: [],
            clients: [],
        };
    }

    componentDidMount() {
        this.getStates();
        this.getDistricts();
        this.getSchools();
        this.getClients();
    }

    private fetchData(requestState: TableRequestDTO, checkEcho: () => boolean, callback: (response: TableResponseDTO<UniversalUserSearchDTO>) => void): void {
        // Add custom filtering into the request
        // Dev Note: We can do Object.keys() as a shortcut because we are using NameOf for the columns which is tied to the backend models
        requestState.filters = Object.keys(this.state.searchParams).map(key => ({ columnProp: key, filter: this.state.searchParams[key] as string }));

        // Business logic time! Ensure that atleast one of the filter has been filled in
        if (!requestState.filters.some(x => x.filter != null && x.filter.toString().length > 0)) {
            // Clear out the datatable as the clear button ends up here as well
            callback({ results: [], filteredCount: 0 } as TableResponseDTO<UniversalUserSearchDTO>);
            return;
        }

        this.setState({ isLoading: true });
        UserApiController.getUniversalUserSearchTableData(requestState)
            .then(results => {
                if (!checkEcho()) {
                    return;
                }

                this.setState({
                    isLoading: false
                });

                callback(results.data);
            });
    }

    private getStates() {
        return StateApiController.getAllStates().then((result) => {
            this.setState({ states: result.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0 ) });
        }).catch(e => {
            message.error(this.props.translate('admin.common.messageStateError'));
        });
    }

    private getDistricts(stateId?: number) {
        let request = stateId == null || isNaN(stateId) ? DistrictApiController.getAllDistricts() : DistrictApiController.getAllDistrictsByState(stateId);
        return request.then((result) => {
            this.setState({ districts: result.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0 ) });
        }).catch(e => {
            message.error(this.props.translate('admin.addEditDistrict.messageRetrieveError'));
        });
    }

    private getClients() {
        let request = ClientApiController.getAllClients();
        return request.then((result) => {
            this.setState({ clients: result.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0 ) });
        }).catch(e => {
            message.error(this.props.translate('admin.addEditDistrict.messageRetrieveError'));
        });
    }

    private getSchools(districtId?: number) {
        let request = districtId == null || isNaN(districtId) ? SchoolApiController.getAllSchools() : SchoolApiController.getAllSchoolsByDistrict(districtId);
        return request.then((result) => {
            this.setState({ schools: result.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0 ) });
        }).catch(e => {
            message.error(this.props.translate('admin.addEditSchool.messageRetrieveError'));
        });
    }

    private getStudentReport(record: UniversalUserSearchDTO): void {
        let reportName = record.firstName + record.lastName + '.xlsx';
        ReportApiController.getStudentReport(record.userId).then(result => {
            message.success(this.props.translate('admin.users.messageReport'));
            downloadFile(result.data, reportName);
        });
    }

    private handleEmulation(record: UniversalUserSearchDTO) {
        let id: number;
        let userType: EmulateUserType;

        var userRole = Roles.FindById(record.roleId);

        switch (userRole) {
            case Roles.StateAdmin:
                id = record.stateId;
                userType = "state";
                break;
            case Roles.DistrictAdmin:
                id = record.districtId;
                userType = "district";
                break;
            case Roles.SchoolAdmin:
                id = record.schoolId;
                userType = "school";
                break;
            case Roles.TeacherCounselorAdmin:
                id = record.schoolId;
                userType = "school";
                break;
        }

        // Fire emulation request, which will take some time, and direct user to emulation loading page
        this.props.emulateUser(id, userType).then(result => {
            if (!result) {
                message.error(this.props.translate("admin.common.messageEmulateError"));
                return;
            }
        });
        this.props.history.push(Routes.ADMIN_EMULATE_USER); // Show them a fancy page while we mess with their emulation info
    }

    private customTableColumns(): DataTableColumnProps<UniversalUserSearchDTO>[] {
        let statementsLink = (record: UniversalUserSearchDTO) => Routes.getRoute(Routes.ADMIN_UNIVERSAL_USER_SEARCH_STUDENT_STATEMENTS, record.userId);
        let changePasswordLink = (record: UniversalUserSearchDTO) => Routes.getRoute(Routes.ADMIN_UNIVERSAL_USER_SEARCH_RESET_PASSWORD, record.userId);
        let editLink = (record: UniversalUserSearchDTO) => Routes.getRoute(Routes.ADMIN_ADD_EDIT_USER, 0, record.userId, Roles.FindById(record.roleId).isAdmin);

        let defaultTooltipProps = {
            mouseLeaveDelay: 0.5,
            mouseEnterDelay: 0.2
        };

        let columns: DataTableColumnProps<UniversalUserSearchDTO>[] = [
            DataTable.BetterColumns.Custom(<Translate id='admin.universalUserSearch.columnHeader8' />, "status", {
                width: 80,
                filterType: "NONE",
                render: (record) => (record.status ? "Active" : "Inactive")
            }),
            DataTable.BetterColumns.Custom(<Translate id='admin.universalUserSearch.columnHeader11' />, "roleId", {
                width: 80,
                filterType: "NONE",
                render: (record) => (Roles.FindById(record.roleId).display)
            }),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader1' />, "firstName", 110),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader2' />, "lastName", 110),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader3' />, "email", 160),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader4' />, "stateName", 100),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader5' />, "districtName", 100),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader6' />, "schoolName", 100),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader7' />, "clientName", 100),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader9' />, "studentId", 110),
            DataTable.AdvancedColumns.Text(<Translate id='admin.universalUserSearch.columnHeader10' />, "gradeName", 80),
            DataTable.BetterColumns.FullManual(null, "actions", {
                align: "right",
                width: 160,
                filterType: "NONE",
                sorter: false,
                // fixed: "right", // Dev Note: This can force some of the columns to stay in place if we add a horizontal scroll
                render: (record) => {
                    let buttons = [];
                    // Admin or Student?
                    if (record.roleId !== Roles.Student.id) {
                        // Emulate
                        buttons.push(
                            <Popover trigger="hover" placement="bottom" content={<Translate id='admin.common.emulateColumnHeader' />}>
                                <Button shape="circle" icon="user" onClick={() => this.handleEmulation(record)} />
                            </Popover>);
                    } else {
                        // I Statements
                        buttons.push(
                            <Popover trigger="hover" placement="bottom" content={<Translate id='admin.users.columnHeader3' />}>
                                <Link to={{ pathname: statementsLink(record), state: this.state.searchParams }}>
                                    <Button shape="circle" icon="profile" />
                                </Link>
                            </Popover>);
                        // Student Report
                        buttons.push(
                            <Popover trigger="hover" placement="bottom" content={<Translate id='admin.users.columnHeader8' />}>
                                <Button shape="circle" icon="download" onClick={() => this.getStudentReport(record)} />
                            </Popover>);
                    }

                    // Dev Note: Allow edit & password reset for Clever/ClassLink users, but not St or Te
                    if ((record.isClever || record.isClassLink) && [Roles.Student.id, Roles.TeacherCounselorAdmin.id].some(x => x === record.roleId)) {
                        // Password reset
                        buttons.push(
                            <Popover trigger="hover" placement="bottom" content={"Cannot reset password of a Clever or ClassLink user"}>
                                <Button shape="circle" icon="lock" disabled />
                            </Popover>);
                        // Edit
                        buttons.push(
                            <Popover trigger="hover" placement="bottom" content={"Cannot edit a Clever or ClassLink user"}>
                                <Button shape="circle" icon="edit" disabled />
                            </Popover>);
                    } else {
                        // Password reset
                        buttons.push(
                            <Popover trigger="hover" placement="bottom" content={<Translate id='admin.users.columnHeader7' />}>
                                <Link to={{ pathname: changePasswordLink(record), state: { previousLocation: this.props.location.pathname, previousState: this.state.searchParams } as ResetPasswordStateParams }}>
                                    <Button shape="circle" icon="lock" />
                                </Link>
                            </Popover>);
                        // Edit
                        buttons.push(
                            <Popover trigger="hover" placement="bottom" content={<Translate id='admin.common.editColumnHeader' />}>
                                <Link to={{ pathname: editLink(record), state: { previousLocation: this.props.location.pathname, previousState: this.state.searchParams } as AddEditUserStateParams }}>
                                    <Button shape="circle" icon="edit" />
                                </Link>
                            </Popover>);
                    }

                    // And render
                    return <Row type="flex" justify="end" gutter={4}>
                        {buttons.map((x, index) => <Col key={index}>{x}</Col>)}
                    </Row>;
                }
            })
        ];

        // Turn off all filtering with this beautiful one-liner
        columns.forEach(col => col.filterType = Filterer.NONE);

        return columns;
    }

    private onSubmit(searchParams: UnitSearchProps) {
        this.setState({ searchParams }, () => this.standardDataTableRef.refresh());
    }

    private onReset() {
        this.getStates();
        this.getDistricts();
        this.setState({ searchParams: {} }, () => this.standardDataTableRef.refresh());
    }

    private onStateFilter(value: string) {
        this.getDistricts(Number(value)).then(() => {
            this.setState({ isFilterLoading: false });
        });
    }

    private onDistrictFilter(value: string) {
        this.getSchools(Number(value)).then(() => {
            this.setState({ isFilterLoading: false });
        });
    }

    render() {
        return (
            <div className="universal-user-search">
                <h2><Translate id="admin.universalUserSearch.title" /></h2>

                <UniversalUserSearchForm
                    isLoading={this.state.isLoading}
                    isFilterLoading={this.state.isFilterLoading}
                    states={this.state.states}
                    districts={this.state.districts}
                    schools={this.state.schools}
                    clients={this.state.clients}
                    onSubmit={this.onSubmit}
                    onReset={this.onReset}
                    onStateFilter={this.onStateFilter}
                    onDistrictFilter={this.onDistrictFilter} />

                <DataTable
                    ref={ref => this.standardDataTableRef = ref}
                    serverSide={true}
                    tableProps={{
                        rowKey: NameOf<UniversalUserSearchDTO>("userId"),
                        loading: this.state.isLoading,
                        pagination: { pageSize: 100, showTotal: DataTable.StandardTotalsRenderer },
                        // scroll: { x: 1100 }
                    }}
                    columns={this.customTableColumns()}
                    data={[]}
                    fetchData={this.fetchData}
                />
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return ({
        emulateUser: (id: number, type: EmulateUserType) => UserAction.EmulateUser(dispatch, type, id),
    });
}

export default connect(null, mapDispatchToProps)(withLocalize(Form.create()(UniversalUserSearch)));



// WEBPACK FOOTER //
// ./src/pages/admin/UniversalUserSearch.tsx